import React, { useState, useEffect } from 'react';

interface DesktopIcon {
  id: string;
  label: string;
  icon: string;
  program: string;
  position: number;
}

const AVAILABLE_ICONS = [
  '📄', '📁', '🖼️', '🎵', '🎬', '🎮', '🌐', '🗑️', '⚠️', '💾',
  '📊', '📈', '📉', '📦', '📧', '📝', '📋', '📌', '📍', '🔧',
  '🔨', '⚙️', '🖥️', '💻', '⌨️', '🖱️', '🖨️', '📱', '📞', '🔔',
  '🔊', '🔇', '🎧', '🎤', '🎸', '🎹', '🎨', '🖌️', '✏️', '📐',
  '📏', '📚', '📖', '📕', '📗', '📘', '📙', '🔒', '🔓', '🔑',
  '⭐', '💡', '🔦', '🔮', '🎯', '🎲', '🃏', '🎰', '🏆', '🥇'
];

const IconManager: React.FC = () => {
  const [icons, setIcons] = useState<DesktopIcon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  useEffect(() => {
    fetchIcons();
  }, []);

  const fetchIcons = async () => {
    try {
      const response = await fetch('/api/desktop/icons');
      const data = await response.json();
      setIcons(data.icons.sort((a: DesktopIcon, b: DesktopIcon) => a.position - b.position));
      setLoading(false);
    } catch (err) {
      setError('Failed to load desktop icons');
      setLoading(false);
    }
  };

  const handleEdit = (icon: DesktopIcon) => {
    setEditingId(icon.id);
    setSelectedIcon(icon.icon);
    setSelectedLabel(icon.label);
    setError('');
    setSuccess('');
  };

  const handleSave = async (iconId: string) => {
    if (!selectedLabel.trim()) {
      setError('Label cannot be empty');
      return;
    }

    const updatedIcons = icons.map(icon =>
      icon.id === iconId
        ? { ...icon, label: selectedLabel, icon: selectedIcon }
        : icon
    );

    await saveIcons(updatedIcons);
    setEditingId(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setSelectedIcon('');
    setSelectedLabel('');
    setError('');
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const newIcons = [...icons];
    [newIcons[index - 1], newIcons[index]] = [newIcons[index], newIcons[index - 1]];

    // Update positions
    const updatedIcons = newIcons.map((icon, idx) => ({
      ...icon,
      position: idx
    }));

    saveIcons(updatedIcons);
  };

  const handleMoveDown = (index: number) => {
    if (index === icons.length - 1) return;

    const newIcons = [...icons];
    [newIcons[index], newIcons[index + 1]] = [newIcons[index + 1], newIcons[index]];

    // Update positions
    const updatedIcons = newIcons.map((icon, idx) => ({
      ...icon,
      position: idx
    }));

    saveIcons(updatedIcons);
  };

  const saveIcons = async (updatedIcons: DesktopIcon[]) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/desktop/icons', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ icons: updatedIcons })
      });

      if (!response.ok) {
        throw new Error('Failed to save icons');
      }

      setSuccess('Icons updated successfully');
      fetchIcons();
    } catch (err) {
      setError('Failed to save icons');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="icon-manager">
      <h3>Desktop Icon Manager</h3>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="window" style={{ padding: '15px' }}>
        <table className="interactive" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '60px' }}>Icon</th>
              <th>Label</th>
              <th>Program</th>
              <th style={{ width: '200px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {icons.map((icon, index) => (
              <tr key={icon.id}>
                <td style={{ fontSize: '32px', textAlign: 'center' }}>
                  {editingId === icon.id ? (
                    <select
                      value={selectedIcon}
                      onChange={(e) => setSelectedIcon(e.target.value)}
                      style={{ fontSize: '24px', width: '60px', height: '40px' }}
                    >
                      {AVAILABLE_ICONS.map(emoji => (
                        <option key={emoji} value={emoji}>{emoji}</option>
                      ))}
                    </select>
                  ) : (
                    icon.icon
                  )}
                </td>
                <td>
                  {editingId === icon.id ? (
                    <input
                      type="text"
                      value={selectedLabel}
                      onChange={(e) => setSelectedLabel(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    icon.label
                  )}
                </td>
                <td>{icon.program}</td>
                <td>
                  {editingId === icon.id ? (
                    <>
                      <button onClick={() => handleSave(icon.id)} style={{ marginRight: '5px' }}>Save</button>
                      <button onClick={handleCancel}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleEdit(icon)} style={{ marginRight: '5px' }}>Edit</button>
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        style={{ marginRight: '5px' }}
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={index === icons.length - 1}
                      >
                        ▼
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="window" style={{ padding: '15px', marginTop: '20px' }}>
        <h4>Available Icons</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '32px' }}>
          {AVAILABLE_ICONS.map(emoji => (
            <span key={emoji} style={{ cursor: 'pointer' }} title={emoji}>
              {emoji}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IconManager;
